/**
 * Orders Filters Component
 * Filter controls for the Orders Management page
 * Extracted from OrdersManagement.jsx
 */
import React from 'react';
import { Calendar } from 'lucide-react';
import { Checkbox } from '../../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { nRole } from '../../../utils/roleNormalizer';

/**
 * Year and Order Type Filter Bar
 * Shows year filter, order type filter (admin only), and unassigned filter (admin/team leaders)
 */
export const OrderFiltersBar = ({
  user,
  yearFilter,
  setYearFilter,
  availableYears,
  loadingYears,
  orderTypeFilter,
  setOrderTypeFilter,
  unassignedOnly,
  setUnassignedOnly,
  onFilterChange,
  onOrderTypeFilterChange,
  // MLS-VIEW-SPLIT: Tax TL working-mode switch (rendered here, in the
  // upper filter bar — behavior lives in OrdersManagement.handleMlsViewSwitch)
  mlsView,
  onMlsViewSwitch,
  // OD-COMPACT-001: render inline (no card chrome) inside the Order Desk toolbar
  compact = false,
  embedded = false
}) => {
  const isAdmin = nRole(user?.role) === 'admin';
  const isTeamLeader = ['admin', 'tax_search_team_leader', 'title_search_team_leader', 'team_leader'].includes(nRole(user?.role));
  const isTaxTL = nRole(user?.role) === 'tax_search_team_leader';

  const handleYearChange = (value) => {
    const newYear = value === 'all' ? 'all' : value;
    setYearFilter(newYear);
    onFilterChange?.({ year: newYear });
  };

  const handleOrderTypeChange = (type) => {
    setOrderTypeFilter(type);
    onOrderTypeFilterChange?.(type);
    onFilterChange?.({ orderType: type });
  };

  const handleUnassignedChange = (checked) => {
    setUnassignedOnly(checked);
    onFilterChange?.({ unassignedOnly: checked });
  };

  return (
    <div className={compact ? 'flex items-center gap-4' : embedded ? 'flex flex-wrap items-center gap-x-6 gap-y-2' : 'flex items-center gap-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200'}>
      {/* Year Filter - Available to all roles */}
      <div className={compact ? 'flex items-center gap-2' : 'flex items-center gap-3'}>
        <Calendar className="h-4 w-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">Year:</span>
        <Select
          value={String(yearFilter)}
          onValueChange={handleYearChange}
          disabled={loadingYears}
        >
          <SelectTrigger className={compact ? 'w-[110px] h-9 text-sm' : 'w-[130px] h-9 rounded-lg border-slate-200 bg-white shadow-sm'} data-testid="year-filter-select">
            <SelectValue placeholder={loadingYears ? "Loading..." : "Select Year"} />
          </SelectTrigger>
          <SelectContent>
            {availableYears.map((year) => (
              <SelectItem key={year} value={String(year)}>
                {year === 'all' ? 'All Years' : year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Separator between Year and Order Type filters (Admin only) */}
      {isAdmin && (
        <div className="h-8 w-px bg-gray-200" />
      )}
      
      {/* Admin Order Type Filter */}
      {isAdmin && (
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700">Order Type:</span>
          <div className="flex gap-2">
            {['all', 'tax', 'mls', 'search'].map((type) => (
              <button
                key={type}
                data-testid={`order-type-filter-${type}`}
                onClick={() => handleOrderTypeChange(type)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  orderTypeFilter === type
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                {type === 'all' ? 'All Orders' : type === 'tax' ? 'Taxes' : type === 'mls' ? 'MLS' : 'Title Search'}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* MLS-VIEW-SPLIT: Tax TL working-mode segmented switch — belongs to
          the upper-level filter area (Year / Unassigned Only), not the
          record-level table filters. */}
      {isTaxTL && onMlsViewSwitch && (
        <>
          <div className="h-8 w-px bg-gray-200" />
          <div className="flex items-center rounded-lg border border-blue-300 overflow-hidden" data-testid="tl-view-switch">
            <button
              type="button"
              data-testid="tl-view-tax"
              onClick={() => onMlsViewSwitch('tax')}
              className={`px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${mlsView === 'tax' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-blue-50'}`}
            >
              TAX ORDERS
            </button>
            <button
              type="button"
              data-testid="tl-view-mls"
              onClick={() => onMlsViewSwitch('mls')}
              className={`px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${mlsView === 'mls' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-blue-50'}`}
            >
              MLS ORDERS
            </button>
          </div>
        </>
      )}

      {/* Unassigned Only Filter - Available to Admin and Team Leaders */}
      {isTeamLeader && (
        <>
          <div className="h-8 w-px bg-gray-200" />
          <div className="flex items-center gap-3">
            <label 
              htmlFor="unassigned-only-toggle"
              className="flex items-center gap-2 cursor-pointer"
            >
              <Checkbox
                id="unassigned-only-toggle"
                data-testid="unassigned-only-toggle"
                checked={unassignedOnly}
                onCheckedChange={handleUnassignedChange}
              />
              <span className="text-sm font-medium text-gray-700">Unassigned Only</span>
            </label>
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Status Tabs Component
 * Tab-based status filter for orders
 */
export const StatusTabs = ({
  statusFilter,
  setStatusFilter,
  statusCounts,
  onStatusChange,
  user
}) => {
  // Define status tabs based on user role
  const getStatusTabs = () => {
    const baseTabs = [
      { value: '', label: 'All', count: statusCounts?.total || 0 },
      { value: 'Open', label: 'Open', count: statusCounts?.open || 0 },
      { value: 'Pending', label: 'Pending', count: statusCounts?.pending || 0 },
      { value: 'Mail-Away', label: 'Mail-Away', count: statusCounts?.mailAway || 0 },
      { value: 'Mail-Away Received', label: 'Mail-Away Received', count: statusCounts?.mailAwayReceived || 0 },
      { value: 'Complete', label: 'Complete', count: statusCounts?.complete || 0 },
      { value: 'Clarification', label: 'Clarification', count: statusCounts?.clarification || 0 },
      { value: 'Clarification Received', label: 'Clarification Received', count: statusCounts?.clarificationReceived || 0 },
    ];
    
    // Add Cancelled tab only for admin
    if (user?.role === 'admin') {
      baseTabs.push({ value: 'Cancelled', label: 'Cancelled', count: statusCounts?.cancelled || 0 });
    }
    
    return baseTabs;
  };

  const tabs = getStatusTabs();

  const handleStatusClick = (status) => {
    setStatusFilter(status);
    onStatusChange?.(status);
  };

  return (
    <div className="flex flex-wrap gap-2 bg-white p-3 rounded-lg shadow-sm border border-gray-200">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          onClick={() => handleStatusClick(tab.value)}
          data-testid={`status-tab-${tab.value || 'all'}`}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            statusFilter === tab.value
              ? 'bg-indigo-600 text-white shadow-md'
              : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
          }`}
        >
          {tab.label}
          {tab.count > 0 && (
            <span className={`px-2 py-0.5 rounded-full text-xs ${
              statusFilter === tab.value
                ? 'bg-white/20 text-white'
                : 'bg-gray-200 text-gray-600'
            }`}>
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

export default OrderFiltersBar;
